import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, MapPin, Activity, Home, ArrowUpRight, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";
import api from "@/lib/api";

const TYPE_COLORS = {
  "Ultra Premium": "bg-purple-100 text-purple-700",
  "Premium": "bg-amber-100 text-[#B88C2E]",
  "Heritage Premium": "bg-rose-100 text-rose-700",
  "Mid": "bg-blue-100 text-blue-700",
  "Affordable": "bg-emerald-100 text-emerald-700",
  "Emerging": "bg-orange-100 text-orange-700",
};

function formatPpsf(v, currency) {
  if (currency && currency.startsWith("AED")) return `AED ${v.toLocaleString()}`;
  if (currency && currency.startsWith("SGD")) return `SGD ${v.toLocaleString()}`;
  return `₹${v.toLocaleString()}`;
}

export default function PropertyInsights() {
  const [cities, setCities] = useState([]);
  const [selected, setSelected] = useState("Lucknow");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/insights").then((r) => { if (!cancel) setCities(r.data.cities || []); });
    api.get("/insights", { params: { city: selected } }).then((r) => {
      if (!cancel) { setDetail(r.data); setLoading(false); }
    }).catch(() => setLoading(false));
    return () => { cancel = true; };
  }, [selected]);

  return (
    <div data-testid="insights-page" className="min-h-screen pb-24">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="overline mb-3 text-[#B88C2E]">Market Intelligence · Q1 2026</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight">
            Property <span className="text-[#D4A437]">Price Insights</span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-3xl text-base sm:text-lg font-light">
            Live capital values, year-on-year growth and demand-supply heatmaps across 10 Indian and global metros.
            Every number researched. Every trend explained.
          </p>
        </div>
      </div>

      {/* City strip — clickable cards */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10">
        <div className="overline mb-4">Compare Cities</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="insights-city-grid">
          {cities.map((c) => {
            const isActive = c.city === selected;
            return (
              <button
                key={c.city}
                onClick={() => setSelected(c.city)}
                data-testid={`insights-city-${c.city.toLowerCase()}`}
                className={`text-left p-4 rounded-xl border transition-all ${
                  isActive
                    ? "bg-slate-900 border-slate-900 text-white shadow-lg"
                    : "bg-white border-slate-200 hover:border-[#D4A437] hover:shadow-md"
                }`}
              >
                <div className={`text-sm font-semibold ${isActive ? "text-white" : "text-slate-900"}`}>{c.city}</div>
                <div className={`font-serif text-lg mt-1 ${isActive ? "text-[#D4A437]" : "text-slate-700"}`}>
                  {formatPpsf(c.avg_ppsf, c.currency)}
                </div>
                <div className={`text-xs mt-1 inline-flex items-center gap-1 ${c.yoy_growth > 12 ? "text-emerald-500" : isActive ? "text-amber-200" : "text-slate-500"}`}>
                  <ArrowUpRight className="w-3 h-3" /> {c.yoy_growth}% YoY
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail */}
      {loading || !detail ? (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 text-slate-500" data-testid="insights-loading">Loading…</div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 space-y-10">
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="insights-kpi-strip">
            <KPI label="Avg Price / sqft" value={formatPpsf(detail.avg_ppsf, detail.currency)} icon={Home} accent />
            <KPI label="YoY Growth" value={`+${detail.yoy_growth}%`} icon={TrendingUp} positive />
            <KPI label="Rental Yield" value={`${detail.rental_yield}%`} icon={Activity} />
            <KPI label="Active Listings" value={detail.active_listings ?? 0} icon={MapPin} />
          </div>

          {/* Outlook */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl p-8 text-white" data-testid="insights-outlook">
            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-[#D4A437] mt-1 shrink-0" />
              <div>
                <div className="overline text-[#D4A437] mb-2">2026 Market Outlook</div>
                <p className="text-slate-200 text-lg leading-relaxed font-light">{detail.outlook}</p>
                <div className="flex gap-6 mt-6 text-sm">
                  <div><span className="text-slate-400">Demand</span> <span className="text-emerald-300 font-semibold ml-1">{detail.demand_index}/100</span></div>
                  <div><span className="text-slate-400">Supply</span> <span className="text-amber-300 font-semibold ml-1">{detail.supply_index}/100</span></div>
                  <div><span className="text-slate-400">Absorption</span> <span className="text-white font-semibold ml-1">{detail.absorption_months} mo</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Locality table + chart */}
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="overline">Top Localities · {detail.city}</div>
                  <div className="text-slate-500 text-xs mt-1">Sorted by capital value</div>
                </div>
              </div>
              <div className="overflow-x-auto" data-testid="insights-locality-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="py-3 font-medium">Locality</th>
                      <th className="py-3 font-medium">Tier</th>
                      <th className="py-3 font-medium text-right">Price/sqft</th>
                      <th className="py-3 font-medium text-right">YoY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.top_localities.map((loc) => (
                      <tr key={loc.name} className="border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
                        <td className="py-4 font-medium text-slate-900">{loc.name}</td>
                        <td className="py-4">
                          <span className={`text-[10px] px-2 py-1 rounded-full font-semibold tracking-wide ${TYPE_COLORS[loc.type] || "bg-slate-100 text-slate-700"}`}>
                            {loc.type}
                          </span>
                        </td>
                        <td className="py-4 text-right font-serif text-slate-900">{formatPpsf(loc.ppsf, detail.currency)}</td>
                        <td className="py-4 text-right text-emerald-600 font-semibold">+{loc.yoy}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6">
              <div className="overline mb-4">Locality Price Comparison</div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={detail.top_localities} layout="vertical" margin={{ left: 0, right: 16 }}>
                    <CartesianGrid stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="name" stroke="#64748b" tick={{ fill: "#334155", fontSize: 11 }} width={120} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, color: "#f8fafc" }}
                      formatter={(v) => formatPpsf(v, detail.currency)}
                    />
                    <Bar dataKey="ppsf" radius={[0, 6, 6, 0]}>
                      {detail.top_localities.map((_, i) => (
                        <Cell key={i} fill={i % 2 === 0 ? "#D4A437" : "#0f172a"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="font-serif text-2xl text-slate-900">Want a free property valuation?</div>
              <div className="text-slate-600 mt-1 text-sm">Get an instant estimate based on this market data.</div>
            </div>
            <Link
              to="/valuation"
              data-testid="insights-cta-valuation"
              className="px-6 py-3 bg-[#D4A437] hover:bg-[#B88C2E] text-white text-sm font-semibold rounded-lg transition-colors text-center"
            >
              Try Property Valuation →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent, positive }) {
  return (
    <div className={`rounded-xl p-5 border ${accent ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className={`overline ${accent ? "text-[#D4A437]" : ""}`}>{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#D4A437]" : positive ? "text-emerald-500" : "text-slate-400"}`} />
      </div>
      <div className={`font-serif text-3xl mt-2 ${accent ? "text-[#D4A437]" : positive ? "text-emerald-600" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
